import { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import ThemeSwitcher from '@components/ThemeSwitcher';
import useAuth from '@hooks/use-auth';
import { Role } from '@interfaces/role';
import { Avatar, Layout, Menu, Tag, Tooltip } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type { FC } from 'react';
import { memo } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import styles from './AuthenticatedLayout.module.scss';

interface OwnProps {
  currentPath: string;
}

const InnerLayout: FC<OwnProps> = memo(function InnerLayout({ currentPath }: OwnProps) {
  const { signOut, hasRole, getAuthorName, getOrganizationName } = useAuth();

  // Read the app version injected by Vite from package.json
  const appVersion = import.meta.env.PACKAGE_VERSION;

  return (
    <Layout className={styles.wrapper}>
      <Layout.Header className={styles.header}>
        <Menu
          mode="horizontal"
          selectedKeys={['chat', 'management'].filter((key) => currentPath.startsWith(`/${key}`))}
          onClick={(info) => {
            if (info.key === 'logout') {
              signOut();
            }
          }}
          items={[
            {
              label: (
                <NavLink to="/chat/conversations" viewTransition>
                  Conversations
                </NavLink>
              ),
              key: 'chat',
            },
            ...(hasRole(Role.Admin)
              ? [
                  {
                    label: (
                      <NavLink to="/management" viewTransition>
                        Management
                      </NavLink>
                    ),
                    key: 'management',
                  } as ItemType,
                ]
              : []),
            {
              label: <ThemeSwitcher />,
              key: 'theme-switcher',
              style: { marginLeft: 'auto' },
              className: styles.withoutHoverEffect,
            },
            ...(currentPath.startsWith('/management')
              ? [
                  {
                    label: <span>{getOrganizationName()}</span>,
                    key: 'org-label',
                    disabled: true,
                  },
                ]
              : []),
            {
              label: <Avatar icon={<UserOutlined />} />,
              key: 'account',
              children: [
                { label: <span>{getAuthorName()}</span>, key: 'user-name', disabled: true },
                {
                  label: (
                    <Tooltip title="Frontend version">
                      <Tag color="default" style={{ marginInlineEnd: 0 }}>
                        v{appVersion}
                      </Tag>
                    </Tooltip>
                  ),
                  key: 'app-version',
                  disabled: true,
                  icon: <InfoCircleOutlined />,
                },
                {
                  label: (
                    <NavLink to="/user/messages" viewTransition>
                      My Messages
                    </NavLink>
                  ),
                  key: 'own-messages',
                },
                {
                  label: 'Logout',
                  key: 'logout',
                },
              ],
            },
          ]}
        />
      </Layout.Header>
      <Layout className={styles.siteContent}>
        <Outlet />
      </Layout>
    </Layout>
  );
});

const AuthenticatedLayout: FC = () => {
  const { isAuthenticated, requiresTermsAcceptance } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ returnUrl: location }} replace />;
  }

  if (requiresTermsAcceptance()) {
    return <Navigate to="/terms-acknowledgement" state={{ returnUrl: location }} replace />;
  }

  return <InnerLayout currentPath={location.pathname} />;
};

export default AuthenticatedLayout;
